import {
  type ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  type Row,
  type RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import type {
  DataTableColumn,
  DataTableColumnVisibility,
  DataTableExpansion,
  DataTableModel,
  DataTableMultiSelect,
  DataTablePagination,
  DataTablePaginationModel,
  DataTableRowData,
  DataTableRowModel,
} from '../types';

export type DataTableOptions<
  TData,
  TColumns extends readonly DataTableColumn<TData>[] = readonly DataTableColumn<TData>[],
> = {
  columns: TColumns;
  columnVisibility?: DataTableColumnVisibility<TColumns[number]['id']>;
  data?: DataTableRowData<TData>;
  expansion?: DataTableExpansion;
  multiSelect?: DataTableMultiSelect<TData>;
  pagination?: DataTablePagination;
};

function isContainerRow<TData>(row: Row<TData>) {
  return row.originalSubRows !== undefined;
}

function resolveParentSelection<TData>(rows: Row<TData>[], selected: Set<string>) {
  for (const row of rows) {
    if (row.subRows.length === 0) continue;
    resolveParentSelection(row.subRows, selected);
    if (row.subRows.every((subRow) => selected.has(subRow.id))) {
      selected.add(row.id);
    } else {
      selected.delete(row.id);
    }
  }
}

export function useDataTable<
  TData,
  const TColumns extends readonly DataTableColumn<TData>[] = readonly DataTableColumn<TData>[],
>(options: DataTableOptions<TData, TColumns>): DataTableModel<TData, TColumns> {
  const {
    columns,
    columnVisibility,
    data,
    expansion,
    multiSelect,
    pagination: paginationProps,
  } = options;

  const visibleIds = useMemo(
    () => (columnVisibility?.value ? new Set<string>(columnVisibility?.value) : undefined),
    [columnVisibility?.value],
  );

  const visible = useMemo(
    () =>
      visibleIds
        ? columns.filter((col) => col.alwaysVisible || visibleIds.has(col.id))
        : [...columns],
    [columns, visibleIds],
  );

  const toggleVisible = useCallback(
    (id: TColumns[number]['id']) => {
      if (!columnVisibility?.value || !columnVisibility?.onChange) return;
      if (columns.some((col) => col.id === id && col.alwaysVisible)) return;

      columnVisibility?.onChange(
        columnVisibility?.value.includes(id)
          ? columnVisibility?.value.filter((visibleId) => visibleId !== id)
          : [...columnVisibility.value, id],
      );
    },
    [columnVisibility?.value, columnVisibility?.onChange, columns],
  );

  const rowSelection = useMemo<RowSelectionState>(
    () => Object.fromEntries((multiSelect?.value ?? []).map((id) => [id, true])),
    [multiSelect?.value],
  );

  const expanded = useMemo<ExpandedState>(
    () => Object.fromEntries((expansion?.value ?? []).map((id) => [id, true])),
    [expansion?.value],
  );

  const table = useReactTable<TData>({
    data: data?.rows ?? [],
    columns: [],
    getRowId: data?.getRowId ? (row, index) => data.getRowId(row, index) : undefined,
    getSubRows: data?.getSubRows,
    state: {
      expanded,
      rowSelection,
    },
    enableRowSelection: Boolean(multiSelect),
    enableSubRowSelection: true,
    onRowSelectionChange: (updater) => {
      const next = typeof updater === 'function' ? updater(rowSelection) : updater;
      const selected = new Set(Object.keys(next).filter((id) => next[id]));
      resolveParentSelection(table.getCoreRowModel().rows, selected);
      multiSelect?.onChange([...selected]);
    },
    onExpandedChange: (updater) => {
      const next = typeof updater === 'function' ? updater(expanded) : updater;
      expansion?.onChange(
        next === true
          ? table
              .getCoreRowModel()
              .flatRows.filter((row) => row.getCanExpand())
              .map((row) => row.id)
          : Object.keys(next).filter((id) => next[id]),
      );
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const canExpand = Boolean(expansion);

  const modelRows = data ? table.getRowModel().rows : undefined;
  const rows = useMemo<DataTableRowModel<TData>[] | undefined>(
    () =>
      modelRows?.map((row) => ({
        data: row.original,
        id: row.id,
        depth: row.depth,
        hasChildren: row.getCanExpand(),
        expanded: row.getIsExpanded(),
        onToggleExpand: canExpand && row.getCanExpand() ? () => row.toggleExpanded() : undefined,
      })),
    [modelRows, canExpand],
  );

  const rowById = useCallback(
    (id: string): Row<TData> | undefined => table.getCoreRowModel().rowsById[id],
    [table],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: table is stable, so rowSelection is what recomputes this
  const selection = useMemo(() => {
    if (!multiSelect) return undefined;

    const selectedRows = table.getSelectedRowModel().flatRows.filter((row) => !isContainerRow(row));

    const selectAllProps = multiSelect.selectAll;
    const dropSelectAll = () => selectAllProps?.onChange(false);

    return {
      selectedIds: new Set(selectedRows.map((row) => row.id)),
      selectedRows: selectedRows.map((row) => row.original),
      count: selectedRows.length,
      allSelected: table.getIsAllPageRowsSelected(),
      someSelected: table.getIsSomePageRowsSelected(),
      selectAll: selectAllProps && {
        active: selectAllProps.value,
        total: selectAllProps.total ?? paginationProps?.total,
        apply: () => {
          selectAllProps.onChange(true);
          table.toggleAllRowsSelected(true);
        },
      },
      isSelected: (id: string) => rowById(id)?.getIsSelected() ?? false,
      isIndeterminate: (id: string) => rowById(id)?.getIsSomeSelected() ?? false,
      toggle: (id: string) => {
        dropSelectAll();
        rowById(id)?.toggleSelected();
      },
      toggleAll: () => {
        dropSelectAll();
        table.toggleAllPageRowsSelected();
      },
      clear: () => {
        dropSelectAll();
        table.resetRowSelection();
      },
    };
  }, [multiSelect, rowSelection, table, rowById, paginationProps?.total]);

  const pagination = useMemo<DataTablePaginationModel | undefined>(() => {
    if (!paginationProps) return undefined;
    const { page, total, pageSize, pageSizeOptions } = paginationProps;
    return {
      page,
      totalPages: Math.max(1, Math.ceil(total / pageSize)),
      total,
      pageSize,
      pageSizeOptions,
      setPage: (next) => paginationProps.onPageChange?.({ page: next, pageSize }),
      setPageSize: (next) => paginationProps.onPageChange?.({ page: 1, pageSize: next }),
    };
  }, [paginationProps]);

  return useMemo(
    () => ({
      columns: { visible, toggleVisible },
      rows,
      isTree: Boolean(data?.getSubRows),
      selection,
      pagination,
    }),
    [visible, toggleVisible, rows, data?.getSubRows, selection, pagination],
  );
}
